import React, { useState } from 'react';
import { Upload, FileSpreadsheet, Check, X, AlertCircle, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import ApiService from '../services/ApiService';

const WorkOrderImport = ({ onClose, onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const [xeroMatchResults, setXeroMatchResults] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState(1); // 1: Upload, 2: Preview, 3: Xero Check, 4: Results
  const [checkXeroExisting, setCheckXeroExisting] = useState(true);

  const handleFileUpload = async (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    setFile(uploadedFile);
    setIsProcessing(true);

    try {
      const data = await readExcelFile(uploadedFile);
      const workOrders = parseWorkOrderData(data, uploadedFile.name);
      setParsedData(workOrders);
      setStep(2);
    } catch (error) {
      console.error('Error parsing file:', error);
      alert('Error parsing file: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsBinaryString(file);
    });
  };

  const parseWorkOrderData = (data, fileName = 'imported file') => {
    if (data.length < 2) {
      throw new Error('File must contain at least a header row and one data row');
    }

    const headers = data[0];
    const workOrders = [];

    console.log('🔍 DEBUG: Headers found in file:', headers);
    console.log('🔍 DEBUG: Total rows in file:', data.length);

    // Create a mapping of expected columns
    const columnMapping = {
      'workOrderNumber': ['work order', 'workorder', 'wo number', 'number', 'id'],
      'clientName': ['client', 'customer', 'client name', 'customer name', 'company'],
      'subcontractorName': ['subcontractor', 'contractor', 'sub', 'team member', 'worker'],
      'invoiceAmount': ['invoice amount', 'amount', 'total', 'price', 'invoice'],
      'cost': ['cost', 'expense', 'contractor cost', 'sub cost'],
      'location': ['location', 'address', 'site', 'property'],
      'description': ['description', 'work description', 'details', 'notes', 'service'],
      'status': ['status', 'state', 'progress'],
      'priority': ['priority', 'urgency'],
      'dueDate': ['due date', 'deadline', 'completion date'],
      'department': ['department', 'dept', 'division'],
      'team': ['team', 'assigned to', 'technician'],
      'serviceType': ['service type', 'service', 'type', 'category']
    };

    // Find column indices
    const columnIndices = {};
    Object.keys(columnMapping).forEach(field => {
      const index = headers.findIndex(header => {
        const headerLower = header.toString().toLowerCase().trim();
        return columnMapping[field].some(pattern => 
          headerLower.includes(pattern) || pattern.includes(headerLower)
        );
      });
      if (index !== -1) {
        columnIndices[field] = index;
      }
    });

    console.log('🔍 DEBUG: Column indices found:', columnIndices);

    // Parse data rows
    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      if (!row || row.length === 0) continue;

      // Skip empty rows
      const hasData = row.some(cell => cell !== undefined && cell !== null && cell.toString().trim() !== '');
      if (!hasData) continue;

      try {
        const subcontractorName = getColumnValue(row, columnIndices.subcontractorName, 'Unknown Contractor');
        const cost = parseFloat(getColumnValue(row, columnIndices.cost, 0)) || 0;
        
        const workOrder = {
          workOrderNumber: getColumnValue(row, columnIndices.workOrderNumber, `WO-${Date.now()}-${i}`),
          clientName: getColumnValue(row, columnIndices.clientName, 'Unknown Client'),
          invoiceAmount: parseFloat(getColumnValue(row, columnIndices.invoiceAmount, 0)) || 0,
          location: getColumnValue(row, columnIndices.location, ''),
          description: getColumnValue(row, columnIndices.description, ''),
          status: getColumnValue(row, columnIndices.status, 'Pending'),
          priority: getColumnValue(row, columnIndices.priority, 'Normal'),
          dueDate: parseDate(getColumnValue(row, columnIndices.dueDate, '')),
          department: getColumnValue(row, columnIndices.department, ''),
          team: getColumnValue(row, columnIndices.team, ''),
          serviceType: getColumnValue(row, columnIndices.serviceType, ''),
          notes: `Imported from ${fileName} on ${new Date().toLocaleDateString()}`,
          tags: 'imported',
          // Convert subcontractor info to lineItems array format
          lineItems: [{
            subcontractorName: subcontractorName,
            subcontractorContactId: '', // Will be matched later if needed
            cost: cost,
            description: getColumnValue(row, columnIndices.description, ''),
            serviceType: getColumnValue(row, columnIndices.serviceType, ''),
            notes: ''
          }]
        };

        // Validate required fields
        console.log(`🔍 DEBUG Row ${i}:`, {
          workOrderNumber: workOrder.workOrderNumber,
          clientName: workOrder.clientName,
          subcontractorName: subcontractorName,
          status: workOrder.status,
          hasAllRequired: !!(workOrder.workOrderNumber && workOrder.clientName && subcontractorName)
        });
        
        if (workOrder.workOrderNumber && workOrder.clientName && subcontractorName) {
          workOrders.push(workOrder);
        } else {
          console.warn(`⚠️ Row ${i} skipped - missing required fields`);
        }
      } catch (error) {
        console.warn(`Error parsing row ${i}:`, error);
      }
    }

    console.log('🔍 DEBUG: Final work orders count:', workOrders.length);
    console.log('🔍 DEBUG: Sample work order:', workOrders[0]);
    
    if (workOrders.length === 0) {
      throw new Error('No valid work orders found in the file. Please check the format and required columns.');
    }

    return workOrders;
  };

  const getColumnValue = (row, columnIndex, defaultValue = '') => {
    if (columnIndex === undefined || columnIndex === -1 || !row[columnIndex]) {
      return defaultValue;
    }
    return row[columnIndex].toString().trim();
  };

  const parseDate = (dateString) => {
    if (!dateString) return null;
    
    try {
      // Handle Excel date numbers
      if (!isNaN(dateString) && dateString > 25569) {
        // Excel date to JavaScript date
        const date = new Date((dateString - 25569) * 86400 * 1000);
        return date.toISOString().split('T')[0];
      }
      
      // Handle string dates
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
    } catch (error) {
      console.warn('Error parsing date:', dateString);
    }
    
    return null;
  };

  const handleCheckXeroExisting = async () => {
    setIsProcessing(true);
    
    try {
      console.log('🔍 Checking Xero for existing work orders...');
      
      // Extract work order numbers from parsed data
      const workOrderNumbers = parsedData.map(wo => wo.workOrderNumber).filter(Boolean);
      
      console.log('📋 Checking', workOrderNumbers.length, 'work order numbers in Xero');
      
      // Use the find invoices endpoint to check which ones exist
      const xeroResults = await ApiService.findInvoicesByNumbers(workOrderNumbers);
      
      console.log('✅ Xero check complete:', xeroResults);
      
      // Match results with parsed data
      const matchedData = parsedData.map(wo => {
        const foundInvoice = xeroResults.found.find(found => found.invoiceNumber === wo.workOrderNumber);
        const existsInXero = !!foundInvoice;
        
        return {
          ...wo,
          existsInXero,
          xeroInvoiceId: foundInvoice?.invoiceID,
          xeroAmountDue: foundInvoice?.amountDue,
          xeroTotal: foundInvoice?.total,
          xeroStatus: existsInXero ? 'Found in Xero' : 'Not in Xero',
          recommendedStatus: existsInXero ? 'Invoiced' : (wo.status || 'Completed')
        };
      });
      
      setXeroMatchResults({
        total: parsedData.length,
        foundInXero: xeroResults.found.length,
        notInXero: xeroResults.notFound.length,
        matchedData,
        xeroInvoices: xeroResults.found
      });
      
      setStep(3);
    } catch (error) {
      console.error('Error checking Xero:', error);
      alert('Error checking Xero: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImport = async () => {
    setIsProcessing(true);
    
    try {
      // Use the matched data if Xero check was performed
      let dataToImport = xeroMatchResults ? xeroMatchResults.matchedData : parsedData;
      
      console.log('📤 Importing', dataToImport.length, 'work orders...');
      console.log('🔍 Looking up contact IDs for customers and contractors...');
      
      // Load contacts to match names to IDs
      const contacts = await ApiService.apiCall('/contactenhancement/enhanced-contacts');
      console.log('📞 Loaded', contacts.length, 'contacts for ID matching');
      
      // Enhance work orders with contact IDs
      dataToImport = dataToImport.map(workOrder => {
        // Look up customer contact ID
        const customerContact = contacts.find(c => 
          (c.name || c.Name || '').toLowerCase().includes(workOrder.clientName.toLowerCase()) ||
          workOrder.clientName.toLowerCase().includes((c.name || c.Name || '').toLowerCase())
        );
        
        // Look up contractor contact ID
        const contractorName = workOrder.lineItems?.[0]?.subcontractorName || '';
        const contractorContact = contacts.find(c => 
          (c.name || c.Name || '').toLowerCase().includes(contractorName.toLowerCase()) ||
          contractorName.toLowerCase().includes((c.name || c.Name || '').toLowerCase())
        );
        
        console.log(`🔍 Matching for WO ${workOrder.workOrderNumber}:`);
        console.log(`  Customer: "${workOrder.clientName}" → ${customerContact ? customerContact.name + ' (ID: ' + (customerContact.contactID || customerContact.ContactID) + ')' : 'Not found'}`);
        console.log(`  Contractor: "${contractorName}" → ${contractorContact ? contractorContact.name + ' (ID: ' + (contractorContact.contactID || contractorContact.ContactID) + ')' : 'Not found'}`);
        
        return {
          ...workOrder,
          xeroContactId: customerContact ? (customerContact.contactID || customerContact.ContactID) : null,
          lineItems: workOrder.lineItems?.map(lineItem => ({
            ...lineItem,
            subcontractorContactId: contractorContact ? (contractorContact.contactID || contractorContact.ContactID) : ''
          })) || []
        };
      });
      
      console.log('📤 Starting import with enhanced contact IDs...');
      const result = await ApiService.bulkImportWorkOrders(dataToImport);
      setImportResults(result);
      setStep(4);
      
      if (onImportComplete) {
        onImportComplete(result);
      }
    } catch (error) {
      console.error('Import error:', error);
      alert('Import failed: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadTemplate = () => {
    const template = [
      ['Work Order Number', 'Client Name', 'Subcontractor', 'Invoice Amount', 'Cost', 'Location', 'Description', 'Status', 'Priority', 'Due Date', 'Department', 'Team', 'Service Type'],
      ['WO-001', 'ABC Company', 'John Smith', '500.00', '350.00', '123 Main St', 'Maintenance work', 'Pending', 'Normal', '2025-02-01', 'GAM', 'rich', 'Maintenance'],
      ['WO-002', 'XYZ Corp', 'Jane Doe', '750.00', '500.00', '456 Oak Ave', 'Repair work', 'Assigned', 'High', '2025-01-25', 'MCS', 'garrett', 'Repair']
    ];

    const ws = XLSX.utils.aoa_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Work Orders Template');
    XLSX.writeFile(wb, 'work-orders-template.xlsx');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Import Work Orders</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center space-x-4">
              <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                  1
                </div>
                <span className="ml-2 text-sm font-medium">Upload File</span>
              </div>
              <div className={`flex-1 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'} rounded`}></div>
              <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                  2
                </div>
                <span className="ml-2 text-sm font-medium">Preview Data</span>
              </div>
              <div className={`flex-1 h-1 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'} rounded`}></div>
              <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                  3
                </div>
                <span className="ml-2 text-sm font-medium">Results</span>
              </div>
            </div>
          </div>

          {/* Step 1: Upload */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-blue-800 mb-2">Import Instructions</h3>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Upload an Excel (.xlsx) or CSV file with your work order data</li>
                  <li>• The file should have headers in the first row</li>
                  <li>• Required columns: Work Order Number, Client Name, Subcontractor</li>
                  <li>• Optional columns: Invoice Amount, Cost, Location, Description, Status, Priority, Due Date, Department, Team, Service Type</li>
                </ul>
              </div>

              <div className="flex justify-center mb-4">
                <button
                  onClick={downloadTemplate}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download Template
                </button>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <FileSpreadsheet className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">Choose your Excel or CSV file to import</p>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  Select File
                </label>
              </div>

              {isProcessing && (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="text-gray-600 mt-2">Processing file...</p>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="bg-green-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-green-800 mb-2">Data Preview</h3>
                <p className="text-sm text-green-700">
                  Found {parsedData.length} work orders in the file. Review the data below and click Import to proceed.
                </p>
              </div>

              <div className="overflow-x-auto max-h-96 border border-gray-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left">Work Order #</th>
                      <th className="px-4 py-2 text-left">Client</th>
                      <th className="px-4 py-2 text-left">Subcontractor</th>
                      <th className="px-4 py-2 text-right">Amount</th>
                      <th className="px-4 py-2 text-right">Cost</th>
                      <th className="px-4 py-2 text-right">Profit</th>
                      <th className="px-4 py-2 text-left">Status</th>
                      <th className="px-4 py-2 text-left">Location</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {parsedData.slice(0, 20).map((workOrder, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium">{workOrder.workOrderNumber}</td>
                        <td className="px-4 py-2">{workOrder.clientName}</td>
                        <td className="px-4 py-2">{workOrder.lineItems?.[0]?.subcontractorName || 'N/A'}</td>
                        <td className="px-4 py-2 text-right">${workOrder.invoiceAmount.toFixed(2)}</td>
                        <td className="px-4 py-2 text-right">${(workOrder.lineItems?.[0]?.cost || 0).toFixed(2)}</td>
                        <td className="px-4 py-2 text-right">
                          <span className={workOrder.invoiceAmount - (workOrder.lineItems?.[0]?.cost || 0) >= 0 ? 'text-green-600' : 'text-red-600'}>
                            ${(workOrder.invoiceAmount - (workOrder.lineItems?.[0]?.cost || 0)).toFixed(2)}
                          </span>
                        </td>
                        <td className="px-4 py-2">{workOrder.status}</td>
                        <td className="px-4 py-2">{workOrder.location}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {parsedData.length > 20 && (
                <p className="text-sm text-gray-500 text-center">
                  Showing first 20 rows. {parsedData.length - 20} more rows will be imported.
                </p>
              )}

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  onClick={handleImport}
                  disabled={isProcessing}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Importing...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Import {parsedData.length} Work Orders
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Results */}
          {step === 3 && importResults && (
            <div className="space-y-6">
              <div className={`rounded-lg p-4 ${importResults.errorCount > 0 ? 'bg-yellow-50' : 'bg-green-50'}`}>
                <h3 className={`text-lg font-semibold mb-2 ${importResults.errorCount > 0 ? 'text-yellow-800' : 'text-green-800'}`}>
                  Import Complete
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{importResults.successCount}</p>
                    <p className="text-sm text-gray-600">Successfully Imported</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-600">{importResults.errorCount}</p>
                    <p className="text-sm text-gray-600">Errors</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{importResults.totalProcessed}</p>
                    <p className="text-sm text-gray-600">Total Processed</p>
                  </div>
                </div>
              </div>

              {importResults.errorCount > 0 && (
                <div className="bg-red-50 rounded-lg p-4">
                  <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Errors Encountered
                  </h4>
                  <ul className="text-sm text-red-700 space-y-1 max-h-32 overflow-y-auto">
                    {importResults.errors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {importResults.successCount > 0 && (
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    Successfully Imported Work Orders
                  </h4>
                  <div className="max-h-32 overflow-y-auto">
                    <div className="text-sm text-green-700 space-y-1">
                      {importResults.createdWorkOrders.slice(0, 10).map((workOrder, index) => (
                        <p key={index}>• {workOrder.workOrderNumber} - {workOrder.clientName}</p>
                      ))}
                      {importResults.createdWorkOrders.length > 10 && (
                        <p>• ... and {importResults.createdWorkOrders.length - 10} more</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkOrderImport;

